/*
 *   File : nfo-detail.js
 *   Author : https://evoqins.com
 *   Description : NFO order detail modal (Other Transactions)
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from 'react';

import { Icon } from '../Icon';
import { CustomTooltip } from '../Others';

const MODAL_ID = "nfo-detail-modal";

const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "-") {
        return "-";
    }
    return value;
};

const formatInitiatedDate = (orderDate) => {
    if (!orderDate) return "-";

    const dateMatch = orderDate.match(/^(.*?\d{4})\s+(\d{1,2}:\d{2}\s*(?:AM|PM))$/i);
    return dateMatch ? `${dateMatch[1]} | ${dateMatch[2]}` : orderDate;
};

const getStatusTextClass = (status) => {
    const normalized = status?.toLowerCase();

    if (normalized === "success" || normalized === "successful") {
        return "e-text-green-crayola";
    }
    if (normalized === "failed") return "e-text-maximum-red";
    if (normalized === "pending" || normalized === "in progress") {
        return "e-text-marigold";
    }
    if (normalized === "submitted" || normalized === "awaiting allotment") {
        return "e-text-bleu-de-france";
    }
    return "e-text-raisin-black";
};

const DetailRow = ({ label, value, valueClass = "e-text-smoky-black", truncate = false, tooltipId, isLast = false }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const displayText = displayValue(value);
    const showTooltip = truncate && displayText !== "-" && isTruncated;
    const rowClass = isLast ? "pt-2" : "border-bottom py-2";

    useEffect(() => {
        const el = textRef.current;
        if (!el || !truncate || displayText === "-") {
            setIsTruncated(false);
            return;
        }

        const checkTruncation = () => {
            setIsTruncated(el.scrollWidth > el.clientWidth + 1);
        };

        checkTruncation();
        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(el);

        return () => resizeObserver.disconnect();
    }, [displayText, truncate]);

    return (
        <div className={`d-flex justify-content-between align-items-start gap-3 ${rowClass}`}>
            <p className='mb-0 e-text-dove-gray e-primary-font e-font-16 e-font-weight-400 line-height-24px flex-shrink-0'>
                {label}
            </p>
            {truncate ?
                <div className={`d-flex justify-content-end flex-grow-1 e-primary-font e-font-16 e-font-weight-600 line-height-24px ${valueClass}`}
                    style={{ minWidth: 0 }}>
                    <p ref={textRef}
                        className='mb-0 text-truncate'
                        style={{ maxWidth: '100%', width: 'fit-content' }}
                        data-tooltip-id={showTooltip ? tooltipId : undefined}
                        data-tooltip-content={showTooltip ? displayText : undefined}>
                        {displayText}
                    </p>
                    {showTooltip &&
                        <CustomTooltip id={tooltipId}
                            icon={false}
                            content={displayText}
                            place="bottom-end"
                            offset={4}
                            width="auto"
                            maxWidth="320px"
                            padding="0.5rem 0.75rem" />
                    }
                </div>
                :
                <p className={`mb-0 text-end text-break flex-grow-1 e-primary-font e-font-16 e-font-weight-600 line-height-24px ${valueClass}`}
                    style={{ minWidth: 0 }}>
                    {displayText}
                </p>
            }
        </div>
    );
};

const DetailCard = ({ title, rows, className = "" }) => (
    <div className={`border-radius-8 p-3 ${className}`} style={{ backgroundColor: "#F5F3FF" }}>
        <p className='mb-2 e-text-dravit-grey e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
            {title}
        </p>

        {rows.map((row, index) => (
            <DetailRow key={row.label}
                label={row.label}
                value={row.value}
                valueClass={row.valueClass}
                truncate={row.truncate}
                tooltipId={row.tooltipId}
                isLast={index === rows.length - 1} />
        ))}
    </div>
);

const NfoDetailModal = ({ data = {}, handleClose = () => { } }) => {
    const historyPushedRef = useRef(false);
    const ignorePopStateRef = useRef(false);

    const orderRows = [
        { label: "Order ID", value: data.order_display_id },
        { label: "FP Order ID", value: data.fp_order_id },
        { label: "Investment type", value: data.type },
        { label: "Order date", value: data.order_date },
        { label: "NFO close date", value: data.nfo_close_date },
        { label: "Scheme", value: data.scheme_name },
        { label: "Order amount", value: data.amount_text },
        { label: "NFO NAV", value: data.allocated_nav !== null ? `₹${data.allocated_nav}` : "-" },
    ];

    const allotmentRows = [
        { label: "Invested amount", value: data.purchase_amount_text },
        {
            label: "Allotment status",
            value: data.order_status,
            valueClass: getStatusTextClass(data.order_status),
        },
        { label: "Allocated units", value: data.allocated_units },
        { label: "Expected allotment date", value: data.expected_allotment_date || "-" },
        {
            label: "Allotment date / NAV date",
            value: (() => {
                const allocationDate = displayValue(data.allocated_date);
                const navDate = displayValue(data.nav_date);
                if (allocationDate === "-" && navDate === "-") return "-";
                return `${allocationDate} / ${navDate}`;
            })(),
        },
        { label: "Folio number", value: data.folio_no }
    ];

    const paymentRows = [
        { label: "Bank account name", value: data.bank_name, truncate: true, tooltipId: "nfo-detail-bank-name" },
        { label: "Payment type", value: data.payment_type || "-" },
        {
            label: "Rayi payment status",
            value: data.payment_status,
            valueClass: getStatusTextClass(data.payment_status),
        },
        {
            label: "Rayi order status",
            value: data.order_status,
            valueClass: getStatusTextClass(data.order_status),
        },
        {
            label: "FP payment status",
            value: data.fp_payment_status,
            valueClass: getStatusTextClass(data.fp_payment_status),
        },
        {
            label: "FP order status",
            value: data.fp_order_status,
            valueClass: getStatusTextClass(data.fp_order_status),
        }
    ];

    const failureRows = [
        { label: "Failed date", value: data.failed_date },
        { label: "Failure reason", value: data.failure_reason, truncate: true, tooltipId: "nfo-detail-failure-reason" },
        { label: "Refund status", value: data.refund_status },
    ];

    const _dismissModal = () => {
        document.getElementById(`${MODAL_ID}-close`)?.click();
        document.querySelector(".modal-backdrop")?.remove();
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "unset";
        handleClose();
    };

    const _handleCloseModal = () => {
        if (historyPushedRef.current) {
            ignorePopStateRef.current = true;
            historyPushedRef.current = false;
            _dismissModal();
            window.history.back();
            return;
        }
        _dismissModal();
    };

    useEffect(() => {
        const modalEl = document.getElementById(MODAL_ID);
        if (!modalEl) return;

        const _handleModalShown = () => {
            if (!historyPushedRef.current) {
                window.history.pushState({ ...(window.history.state || {}), nfoDetailModal: true }, "");
                historyPushedRef.current = true;
            }
        };

        const _handlePopState = () => {
            if (ignorePopStateRef.current) {
                ignorePopStateRef.current = false;
                return;
            }

            if (historyPushedRef.current || modalEl.classList.contains("show")) {
                historyPushedRef.current = false;
                _dismissModal();
            }
        };

        modalEl.addEventListener("shown.bs.modal", _handleModalShown);
        window.addEventListener("popstate", _handlePopState);

        return () => {
            modalEl.removeEventListener("shown.bs.modal", _handleModalShown);
            window.removeEventListener("popstate", _handlePopState);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className='modal fade'
            id={MODAL_ID}
            data-bs-backdrop='static'
            data-bs-keyboard='false'
            tabIndex='-1'
            aria-labelledby='nfoDetailModalLabel'
            aria-hidden='true'>
            <div className='modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable'>
                <span id={`${MODAL_ID}-close`}
                    className='d-none'
                    data-bs-dismiss='modal'>
                </span>

                <div className='modal-content border-0 border-radius-28 p-4'>
                    <div className='modal-header border-0 border-bottom p-0 pb-3 justify-content-between align-items-start'>
                        <div>
                            <h6 id='nfoDetailModalLabel'
                                className='mb-2 e-text-raisin-black e-primary-font e-font-16 e-font-weight-600 line-height-24px'>
                                {displayValue(data.name)}
                            </h6>
                            <p className='mb-0 e-text-davys-grey-light e-primary-font e-font-14 e-font-weight-400 line-height-20px'>
                                {displayValue(data.name)} ({displayValue(data.customer_display_id)}) | Initiated {formatInitiatedDate(data.order_date)}
                            </p>
                        </div>

                        <Icon icon='close'
                            size='1.125rem'
                            className='d-flex cursor-pointer'
                            onClick={_handleCloseModal} />
                    </div>

                    <div className='modal-body px-0 py-3' style={{ overflowX: "hidden" }}>
                        <div className='row g-3 mx-0 align-items-stretch'>
                            <div className='col-12 col-lg-6 d-flex flex-column gap-3'>
                                <DetailCard title='NFO Order Details' rows={orderRows} />
                                <DetailCard title='Payment / Order Details' rows={paymentRows} className='flex-grow-1' />
                            </div>
                            <div className='col-12 col-lg-6 d-flex flex-column gap-3'>
                                <DetailCard title='Allotment Details' rows={allotmentRows} />
                                <DetailCard title='Failure Details' rows={failureRows} />
                            </div>
                        </div>
                    </div>

                    <div className='modal-footer border-0 p-0 justify-content-start'>
                        <p className='mb-0 e-text-davys-grey-light  e-primary-font e-font-14 e-font-weight-400 line-height-20px'>
                            Last updated on {displayValue(data.order_status_updated_on)}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NfoDetailModal;
